// SideStarField.tsx
// Twinkling star field behind a slide (visible in the side gaps around the
// scaled slide). Stars drift away from the mouse and ease back home, under a
// soft teal halo that follows the pointer — the same interaction as the
// yogyism.org hero.
//
// Performance: only the gaps that are not covered by the (opaque) slide are
// cleared and drawn, off-screen slides are skipped, and touch devices — which
// cannot hover — get a 15 fps twinkle without the halo.
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const REPEL_RADIUS   = 280;   // px around the pointer that pushes stars away
const REPEL_FORCE    = 7;     // max push per frame (at the pointer)
const RETURN_EASE    = 0.025; // fraction of the way home per frame
const FRAME_MS       = 1000 / 30; // ambient background — 30 fps is plenty
const FRAME_MS_TOUCH = 1000 / 15; // no hover on touch screens: twinkle only
const HALO_RADIUS    = 230;   // px — 460px glow, as on yogyism.org
const HALO_RGB       = "94,234,212";
const HALO_FADE      = 0.15;  // per-frame ease toward visible/hidden (~0.6 s)

const COLORS = ["rgb(255,255,255)", "rgb(150,200,255)", "rgb(190,160,255)"];

type Star = {
  fx: number; fy: number;   // home position as a fraction of the field
  x: number; y: number;     // current position (px)
  r: number;
  peak: number;             // max opacity
  dur: number;              // twinkle period (s)
  phase: number;
  col: number;              // index into COLORS
};

// Plain function — NOT a hook. Safe to call anywhere.
function makeStars(count: number): Star[] {
  return Array.from({ length: count }, () => ({
    fx: Math.random(),
    fy: Math.random(),
    x: 0, y: 0,
    r: 0.35 + Math.random() * 1.6,
    peak: 0.30 + Math.random() * 0.6,
    dur: 2.4 + Math.random() * 3.8,
    phase: Math.random() * Math.PI * 2,
    col: Math.random() < 0.78 ? 0 : (Math.random() < 0.5 ? 1 : 2),
  }))
    // Grouped by colour so fillStyle changes at most three times per frame.
    .sort((a, b) => a.col - b.col);
}

const StarCanvas: React.FC<{ count: number }> = ({ count }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !host || !ctx) return;

    const stars = makeStars(count);
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const hoverable = window.matchMedia?.("(hover: hover)").matches ?? true;
    const frameMs = hoverable ? FRAME_MS : FRAME_MS_TOUCH;
    let w = 0, h = 0;
    let visible = true;
    let clearedWhileCovered = false;
    // Pointer in viewport coordinates. It is converted to field coordinates
    // every frame, because Swiper moves slides with CSS transforms, which fire
    // no scroll/resize events, so a cached position would go stale.
    const pointer = { clientX: 0, clientY: 0, inWindow: false, x: 0, y: 0, active: false };
    let haloAlpha = 0;

    // The opaque slide drawn on top of the field (largest sibling of the
    // host); stars underneath it are never seen, so they are not drawn.
    const findCover = (): HTMLElement | null => {
      let best: HTMLElement | null = null, bestArea = 0;
      for (const el of Array.from(host.parentElement?.children ?? [])) {
        if (el === host) continue;
        const r = el.getBoundingClientRect();
        if (r.width * r.height > bestArea) { bestArea = r.width * r.height; best = el as HTMLElement; }
      }
      return best;
    };
    let cover = findCover();

    const resize = () => {
      const rect = host.getBoundingClientRect();
      w = rect.width; h = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (const s of stars) { s.x = s.fx * w; s.y = s.fy * h; }
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      pointer.clientX = e.clientX;
      pointer.clientY = e.clientY;
      pointer.inWindow = true;
    };
    const onLeave = () => { pointer.inWindow = false; };
    // Mouse left the window entirely.
    const onOut = (e: MouseEvent) => { if (!e.relatedTarget) onLeave(); };

    let raf = 0, last = 0;
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      if (!visible || document.hidden || now - last < frameMs) return;
      last = now;

      const rect = host.getBoundingClientRect();
      if (!cover || !cover.isConnected) cover = findCover();
      const c = cover?.getBoundingClientRect();
      // Covered area in field coordinates (empty when nothing covers the field).
      const cx0 = c ? c.left - rect.left : 0, cx1 = c ? c.right - rect.left : 0;
      const cy0 = c ? c.top - rect.top : 0,   cy1 = c ? c.bottom - rect.top : 0;
      const hasGap = !c || cx0 > 0.5 || cy0 > 0.5 || cx1 < w - 0.5 || cy1 < h - 0.5;
      if (!hasGap) {
        // Slide fills the whole field: nothing visible to animate.
        if (!clearedWhileCovered) { ctx.clearRect(0, 0, w, h); clearedWhileCovered = true; }
        return;
      }
      clearedWhileCovered = false;

      const px = pointer.clientX - rect.left, py = pointer.clientY - rect.top;
      pointer.active = hoverable && pointer.inWindow && px >= 0 && py >= 0 && px <= w && py <= h;
      // While active, track the pointer; on leaving, the halo fades where it was.
      if (pointer.active) { pointer.x = px; pointer.y = py; }

      // Clear and draw only inside the visible gaps around the slide.
      ctx.save();
      ctx.beginPath();
      if (c) {
        if (cx0 > 0) ctx.rect(0, 0, cx0, h);
        if (cx1 < w) ctx.rect(cx1, 0, w - cx1, h);
        if (cy0 > 0) ctx.rect(cx0, 0, cx1 - cx0, cy0);
        if (cy1 < h) ctx.rect(cx0, cy1, cx1 - cx0, h - cy1);
      } else {
        ctx.rect(0, 0, w, h);
      }
      ctx.clip();
      ctx.clearRect(0, 0, w, h);
      const t = now / 1000;

      // Halo: fades in where the pointer is and fades out in place when it leaves.
      if (hoverable && !reduced) {
        haloAlpha += ((pointer.active ? 1 : 0) - haloAlpha) * HALO_FADE;
        if (haloAlpha > 0.01) {
          const g = ctx.createRadialGradient(pointer.x, pointer.y, 0, pointer.x, pointer.y, HALO_RADIUS);
          g.addColorStop(0,    `rgba(${HALO_RGB},${(0.11 * haloAlpha).toFixed(3)})`);
          g.addColorStop(0.28, `rgba(${HALO_RGB},${(0.06 * haloAlpha).toFixed(3)})`);
          g.addColorStop(0.7,  `rgba(${HALO_RGB},0)`);
          ctx.fillStyle = g;
          ctx.fillRect(pointer.x - HALO_RADIUS, pointer.y - HALO_RADIUS, HALO_RADIUS * 2, HALO_RADIUS * 2);
        }
      }

      let col = -1;
      for (const s of stars) {
        const homeX = s.fx * w, homeY = s.fy * h;
        if (pointer.active) {
          const dx = s.x - pointer.x, dy = s.y - pointer.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < REPEL_RADIUS && d > 0) {
            const f = ((REPEL_RADIUS - d) / REPEL_RADIUS) * REPEL_FORCE;
            s.x += (dx / d) * f;
            s.y += (dy / d) * f;
          }
        }
        s.x += (homeX - s.x) * RETURN_EASE;
        s.y += (homeY - s.y) * RETURN_EASE;
        // Fully under the slide → invisible, skip drawing.
        if (c && s.x - s.r >= cx0 && s.x + s.r <= cx1 && s.y - s.r >= cy0 && s.y + s.r <= cy1) continue;
        // Twinkle between ~22% and 100% of the star's peak opacity.
        ctx.globalAlpha = reduced
          ? s.peak
          : s.peak * (0.61 + 0.39 * Math.sin((t * Math.PI * 2) / s.dur + s.phase));
        if (s.col !== col) { col = s.col; ctx.fillStyle = COLORS[col]; }
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore(); // also resets clip and globalAlpha
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    // Neighbouring slides stay mounted off-screen; don't draw them.
    const io = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
    });
    io.observe(host);
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("mouseout", onOut);
    window.addEventListener("blur", onLeave);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("mouseout", onOut);
      window.removeEventListener("blur", onLeave);
    };
  }, [count]);

  return <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />;
};

const SideStarField: React.FC<{
  rootRef: React.RefObject<HTMLElement | null>;
  count?: number;
}> = ({ rootRef, count = 320 }) => {
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const slide  = rootRef.current?.closest(".swiper-slide") as HTMLElement | null;
    const target = (slide?.querySelector(":scope > div") as HTMLElement | null) ?? slide;
    if (!target) return;

    if (getComputedStyle(target).position === "static") target.style.position = "relative";

    const h = document.createElement("div");
    h.setAttribute("data-starfield", "");
    h.style.cssText = "position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:0;";
    target.insertBefore(h, target.firstChild);
    setHost(h);

    return () => { h.remove(); };
  }, [rootRef]);

  return host ? ReactDOM.createPortal(<StarCanvas count={count} />, host) : null;
};

export default SideStarField;
